import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  LogOut, RefreshCw, Loader2, Mail, Phone, ExternalLink, MessageSquare,
  Inbox, Users2, BriefcaseBusiness, GraduationCap, Quote, Trophy, BadgePercent,
  CalendarDays, CalendarHeart, Store, LayoutDashboard, Settings as SettingsIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { adminLogout, isAuthed, adminMe } from "../lib/admin";
import { adminList } from "../lib/content";
import { toast } from "sonner";
import { BRAND } from "../lib/constants";
import { ResourceManager } from "../components/admin/ResourceManager";
import { SettingsManager } from "../components/admin/SettingsManager";

const NAV = [
  { id: "submissions", label: "Submissions", icon: Inbox },
  { id: "courses", label: "Courses", icon: GraduationCap },
  { id: "testimonials", label: "Testimonials", icon: Quote },
  { id: "stories", label: "Success Stories", icon: Trophy },
  { id: "offers", label: "Offers", icon: BadgePercent },
  { id: "events", label: "Events", icon: CalendarHeart },
  { id: "holidays", label: "Holidays", icon: CalendarDays },
  { id: "settings", label: "Site Settings", icon: SettingsIcon },
];

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function SubmissionsView() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setData(await loadSubs());
    } catch {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const loadSubs = async () => {
    const { adminSubmissions } = await import("../lib/admin");
    return adminSubmissions();
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) return <div className="flex items-center gap-2 text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (!data) return null;

  const Card = ({ icon: Icon, label, value, test }) => (
    <div data-testid={test} className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-xl bg-[#00B37E]/12 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#00B37E]" />
        </div>
        <div className="text-3xl font-heading font-black text-[#0A192F]">{value}</div>
      </div>
      <div className="text-xs uppercase tracking-[0.18em] text-[#64748B] mt-3 font-bold">{label}</div>
    </div>
  );

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={Inbox} label="Contact Enquiries" value={data.counts.contact} test="stat-contact" />
        <Card icon={Users2} label="Mentor Applications" value={data.counts.mentor} test="stat-mentor" />
        <Card icon={BriefcaseBusiness} label="Placement Resumes" value={data.counts.career} test="stat-career" />
        <Card icon={Store} label="Partner Leads" value={data.counts.partner} test="stat-partner" />
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 md:p-7">
        <Tabs defaultValue="contact">
          <TabsList className="bg-slate-100 flex-wrap">
            <TabsTrigger value="contact" data-testid="tab-contact">Contact ({data.counts.contact})</TabsTrigger>
            <TabsTrigger value="mentor" data-testid="tab-mentor">Mentor ({data.counts.mentor})</TabsTrigger>
            <TabsTrigger value="career" data-testid="tab-career">Career ({data.counts.career})</TabsTrigger>
            <TabsTrigger value="partner" data-testid="tab-partner">Partner ({data.counts.partner})</TabsTrigger>
          </TabsList>

          {["contact", "mentor", "career", "partner"].map((kind) => (
            <TabsContent key={kind} value={kind} className="mt-5">
              {data[kind].length === 0 ? (
                <div className="text-sm text-[#64748B] py-8 text-center">No {kind} submissions yet.</div>
              ) : (
                <div className="space-y-3" data-testid={`${kind}-list`}>
                  {data[kind].map((c) => (
                    <article key={c.id} className="border border-slate-200 rounded-xl p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="font-heading font-bold text-[#0A192F]">{c.name}</div>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-[#475569] mt-1">
                            <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-[#00B37E]"><Mail className="w-3.5 h-3.5" />{c.email}</a>
                            <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-[#00B37E]"><Phone className="w-3.5 h-3.5" />{c.phone}</a>
                            {c.linkedin ? <a href={c.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[#00B37E]"><ExternalLink className="w-3.5 h-3.5" />LinkedIn</a> : null}
                            {c.resume_link ? <a href={c.resume_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[#00B37E]"><ExternalLink className="w-3.5 h-3.5" />Resume</a> : null}
                          </div>
                        </div>
                        <span className="text-xs text-[#64748B]">{fmt(c.created_at)}</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {c.expertise ? <span className="px-3 py-1 rounded-full bg-[#00B37E]/12 text-[#00B37E] text-xs font-bold">{c.expertise}</span> : null}
                        {c.experience_years ? <span className="px-3 py-1 rounded-full bg-slate-100 text-[#0A192F] text-xs font-semibold">{c.experience_years}</span> : null}
                        {c.position ? <span className="px-3 py-1 rounded-full bg-[#0A192F] text-white text-xs font-bold">{c.position}</span> : null}
                        {c.experience ? <span className="px-3 py-1 rounded-full bg-slate-100 text-[#0A192F] text-xs font-semibold">{c.experience}</span> : null}
                        {c.city ? <span className="px-3 py-1 rounded-full bg-slate-100 text-[#0A192F] text-xs font-semibold">{c.city}</span> : null}
                        {c.subject ? <span className="px-3 py-1 rounded-full bg-slate-100 text-[#0A192F] text-xs font-semibold">{c.subject}</span> : null}
                      </div>
                      {(c.message || c.cover_note || c.current_business) ? (
                        <p className="mt-3 text-sm text-[#334155] flex gap-2">
                          <MessageSquare className="w-4 h-4 mt-0.5 text-[#00B37E] flex-shrink-0" />
                          <span className="whitespace-pre-wrap">{c.message || c.cover_note || c.current_business}</span>
                        </p>
                      ) : null}
                    </article>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}

export default function Admin() {
  const [section, setSection] = useState("submissions");
  const [authChecked, setAuthChecked] = useState(false);
  const navigate = useNavigate();
  const authed = isAuthed();

  useEffect(() => {
    if (!authed) return;
    adminMe().then(() => setAuthChecked(true)).catch(() => {
      adminLogout(); navigate("/admin/login");
    });
  }, [authed, navigate]);

  if (!authed) return <Navigate to="/admin/login" replace />;
  if (!authChecked) return <div className="min-h-screen flex items-center justify-center text-[#64748B]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;

  const onLogout = () => {
    adminLogout();
    toast.success("Logged out.");
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#F8FAF9]">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="TASSEI" className="h-11 w-auto object-contain" />
            <div>
              <div className="font-heading font-black text-[#0A192F] text-lg tracking-tight">
                TASSEI<span className="text-[#00B37E]">.in</span> · CMS
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] mt-0.5">
                Content &amp; Submissions
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => window.open("/", "_blank")} variant="outline" className="border-[#0A192F] text-[#0A192F] rounded-full h-10" data-testid="admin-view-site">
              <LayoutDashboard className="w-4 h-4 mr-2" /> View site
            </Button>
            <Button onClick={onLogout} className="bg-[#0A192F] hover:bg-[#0F2648] text-white rounded-full h-10" data-testid="admin-logout">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3">
          <nav className="bg-white border border-slate-200 rounded-2xl p-2 sticky top-[88px]" data-testid="admin-sidebar">
            {NAV.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setSection(id)}
                data-testid={`nav-${id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                  section === id ? "bg-[#0A192F] text-white" : "text-[#0A192F] hover:bg-slate-50"
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
            <button
              onClick={() => window.location.reload()}
              className="mt-2 w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-[#64748B] hover:bg-slate-50"
              data-testid="nav-refresh"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </nav>
        </aside>

        <main className="lg:col-span-9">
          {section === "submissions" ? <SubmissionsView /> :
           section === "settings" ? <SettingsManager /> :
           <ResourceManager resource={section} />}
        </main>
      </div>
    </div>
  );
}
